import { Empty, Modal, Typography } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import { useGetBranchInstructionsQuery } from '@app/api/Branches';
import { CloseIconsForModalWithDataTestId } from '@app/components';
import { PREFIX } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import '../styles/CommentModal.desktop.scss';

const { Text } = Typography;

type CommentModalProps = {
  isCommentModal: boolean;
  setIsCommentModal: (value: boolean) => void;
};

export const CommentModal = ({
  isCommentModal,
  setIsCommentModal,
}: CommentModalProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const { data: commentWithMeta, refetch } = useGetBranchInstructionsQuery(
    { ActiveBranchId, forBranchId: ActiveBranchId },
    { skip: !ActiveBranchId }
  );

  useEffect(() => {
    if (isCommentModal && ActiveBranchId !== null) {
      refetch();
    }
  }, [isCommentModal, ActiveBranchId, refetch]);

  const extractMimeType = (name: string) => {
    const handledName = name.toLowerCase();

    switch (true) {
      case handledName.endsWith('.jpeg') || handledName.endsWith('.jpg'):
        return 'image/jpeg';
      case handledName.endsWith('.png'):
        return 'image/png';
      default:
        return undefined;
    }
  };

  const attachment = useMemo((): {
    isExists: boolean;
    component: JSX.Element;
  } => {
    if (!commentWithMeta || !commentWithMeta?.InstructionsFile) {
      return {
        isExists: false,
        component: <></>,
      };
    }

    const { Name, Data } = commentWithMeta.InstructionsFile;

    if (!Data || !Name) {
      return {
        isExists: false,
        component: <></>,
      };
    }

    return {
      isExists: true,
      component: (
        <img
          alt="Изображение инструкции"
          className="attachment"
          src={`data:${extractMimeType(Name)};base64,${Data}`}
          data-testid="note-image"
        />
      ),
    };
  }, [commentWithMeta]);

  const isEmptyContent = useMemo(() => {
    const isEmptyComment = !commentWithMeta?.Comment;
    const isEmptyAttachment = !commentWithMeta?.InstructionsFile?.Data;

    return isEmptyComment && isEmptyAttachment;
  }, [commentWithMeta]);

  const comment = useMemo(() => {
    return commentWithMeta?.Comment;
  }, [commentWithMeta?.Comment]);

  const commentComponent = useMemo(
    () => (
      <>
        {comment && (
          <Text data-testid="note-text" style={{ whiteSpace: 'pre-wrap' }}>
            {comment.split('\\n').map((paragraph, index) => (
              <span key={index}>
                {paragraph}
                {index < comment.split('\\n').length - 1 && <br />}
              </span>
            ))}
          </Text>
        )}
      </>
    ),
    [comment]
  );

  const isAttachmentExists = useMemo(
    () => attachment.isExists,
    [attachment.isExists]
  );

  const handledCommentWithAttachment = useMemo(() => {
    if (comment && isAttachmentExists) {
      return (
        <div className="two-columns-layout">
          <div className="two-columns-layout__column comment-column">
            {commentComponent}
          </div>
          <div className="two-columns-layout__column attachment-column">
            {attachment.component}
          </div>
        </div>
      );
    }

    if (comment && !isAttachmentExists) {
      return commentComponent;
    }

    if (!comment && isAttachmentExists) {
      return <div className="attachment-wrapper">{attachment.component}</div>;
    }

    return <Empty />;
  }, [comment, isAttachmentExists, commentComponent, attachment.component]);

  return (
    <Modal
      destroyOnHidden
      width={comment && isAttachmentExists ? 800 : 400}
      title={generateSpanWithDataTestId('Примечание', 'note-popup-title')}
      className={classNames(`${PREFIX}comment-modal`, {
        ['empty-content']: isEmptyContent,
      })}
      open={isCommentModal}
      footer={null}
      onCancel={() => setIsCommentModal(false)}
      closeIcon={<CloseIconsForModalWithDataTestId />}
    >
      {handledCommentWithAttachment}
    </Modal>
  );
};
